// Contract API của data-api (`/api/internal/v1/workspaces/:ws/...`).
import { IsoDateTime, list, success, Uuid } from "@secretaa/http-contract";
import { z } from "zod";

export const ProjectKey = z.string().regex(/^[A-Z][A-Z0-9]{1,9}$/, "2–10 ký tự A-Z / 0-9, bắt đầu bằng chữ");
export const ProjectStatus = z.enum(["active", "archived"]);

export const Project = z
  .object({
    id: Uuid,
    workspaceId: Uuid,
    orgUnitId: Uuid.nullable(),
    key: z.string(),
    name: z.string(),
    status: ProjectStatus,
    createdBy: Uuid,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    archivedAt: IsoDateTime.nullable(),
    version: z.number().int(),
    /** Vai trò project của người gọi (ADR-010) — UI ẩn Build / quản lý thành viên khi không phải `manager`. */
    myRole: z.enum(["viewer", "editor", "manager"]).nullable(),
  })
  .strict();
export type Project = z.infer<typeof Project>;

/** `id`: projectId Platform đã giữ chỗ (ADR-010 mục 5) — bắt buộc với member khi enforce; owner / admin có thể bỏ trống. */
export const CreateProjectBody = z.object({ id: Uuid.optional(), name: z.string().trim().min(1).max(120), key: ProjectKey, orgUnitId: Uuid.nullable().default(null) }).strict();
export const UpdateProjectBody = z
  .object({ name: z.string().trim().min(1).max(120).optional(), orgUnitId: Uuid.nullable().optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.orgUnitId !== undefined, "cần ít nhất một trường");
export const ListProjectsQuery = z.object({ status: ProjectStatus.optional(), cursor: z.string().max(512).optional(), limit: z.coerce.number().int().min(1).max(100).default(25) }).strict();

export const ProjectResponse = success(Project);
export const ProjectsResponse = list(Project);

// ---------- Project Scope (ADR-005, SAA-2026-005) ----------
export const ScopeType = z.string().regex(/^[a-z][a-z0-9_]{1,31}$/, "chữ thường, số, gạch dưới; bắt đầu bằng chữ");
export const ScopeKey = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "chữ thường, số, - và _");
export const ScopeStatus = z.enum(["active", "suspended", "archived"]);
export const ProjectScope = z
  .object({
    id: Uuid,
    projectId: Uuid,
    parentScopeId: Uuid.nullable(),
    /** Gốc → cha (không gồm chính nó); vật chất hoá từ parentScopeId, chỉ để đọc. */
    ancestorIds: z.array(Uuid),
    depth: z.number().int().min(0).max(8),
    type: z.string(),
    key: z.string(),
    name: z.string(),
    externalId: z.string().nullable(),
    status: ScopeStatus,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
  })
  .strict();
export type ProjectScope = z.infer<typeof ProjectScope>;
const ScopeName = z.string().trim().min(1).max(120);
const ExternalId = z.string().trim().min(1).max(200);
export const CreateScopeBody = z
  .object({ parentScopeId: Uuid.nullable().default(null), type: ScopeType, key: ScopeKey, name: ScopeName, externalId: ExternalId.nullable().default(null) })
  .strict();
export const UpdateScopeBody = z
  .object({ name: ScopeName.optional(), key: ScopeKey.optional() })
  .strict()
  .refine((v) => v.name !== undefined || v.key !== undefined, "cần ít nhất một trường");
export const MoveScopeBody = z.object({ parentScopeId: Uuid.nullable() }).strict();
/** Find-or-create theo externalId (path) — dùng cho App Link / `/v1` (U.3 bước 5). */
export const FindOrCreateScopeBody = z.object({ parentScopeId: Uuid.nullable().default(null), type: ScopeType, key: ScopeKey, name: ScopeName }).strict();
export const ListScopesQuery = z
  .object({
    parentScopeId: Uuid.optional(),
    roots: z.enum(["true"]).optional(),
    status: ScopeStatus.optional(),
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .strict();
export const ScopeResponse = success(ProjectScope);
export const ScopesResponse = list(ProjectScope);

export const ActivityEvent = z
  .object({
    id: Uuid,
    actorType: z.string(),
    actorId: z.string(),
    eventType: z.string(),
    resourceType: z.string(),
    resourceId: z.string().nullable(),
    /** `access.denied`: hành động bị từ chối (vd. `project.update`) — SAA-2026-012. Tuỳ chọn để dash mới đọc được data-api cũ. */
    action: z.string().nullable().optional(),
    createdAt: IsoDateTime,
  })
  .strict();
export const ActivityResponse = list(ActivityEvent);

export const DATA_PATHS = {
  projects: (ws: string) => `/workspaces/${ws}/projects`,
  project: (ws: string, id: string) => `/workspaces/${ws}/projects/${id}`,
  projectArchive: (ws: string, id: string) => `/workspaces/${ws}/projects/${id}/archive`,
  projectRestore: (ws: string, id: string) => `/workspaces/${ws}/projects/${id}/restore`,
  activity: (ws: string) => `/workspaces/${ws}/activity`,
  scopes: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/scopes`,
  scope: (ws: string, p: string, id: string) => `/workspaces/${ws}/projects/${p}/scopes/${id}`,
  scopeMove: (ws: string, p: string, id: string) => `/workspaces/${ws}/projects/${p}/scopes/${id}/move`,
  scopeArchive: (ws: string, p: string, id: string) => `/workspaces/${ws}/projects/${p}/scopes/${id}/archive`,
  scopeByExternalId: (ws: string, p: string, ext: string) => `/workspaces/${ws}/projects/${p}/scopes/by-external-id/${encodeURIComponent(ext)}`,
  collections: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/collections`,
  records: (ws: string, p: string, c: string) => `/workspaces/${ws}/projects/${p}/collections/${c}/records`,
  recordsQuery: (ws: string, p: string, c: string) => `/workspaces/${ws}/projects/${p}/collections/${c}/records/query`,
  projectQuery: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/query`,
  record: (ws: string, p: string, c: string, id: string) => `/workspaces/${ws}/projects/${p}/collections/${c}/records/${id}`,
  recordDelete: (ws: string, p: string, c: string, id: string) => `/workspaces/${ws}/projects/${p}/collections/${c}/records/${id}/delete`,
  recordRestore: (ws: string, p: string, c: string, id: string) => `/workspaces/${ws}/projects/${p}/collections/${c}/records/${id}/restore`,
  blueprintTemplates: (ws: string) => `/workspaces/${ws}/blueprint-templates`,
  blueprint: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/blueprint`,
  blueprintVersions: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/blueprint/versions`,
  blueprintVersion: (ws: string, p: string, v: number) => `/workspaces/${ws}/projects/${p}/blueprint/versions/${v}`,
  blueprintPreview: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/blueprint/preview`,
  blueprintApprovals: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/blueprint/approvals`,
  blueprintApply: (ws: string, p: string) => `/workspaces/${ws}/projects/${p}/blueprint/apply`,
} as const;

export * from "./blueprint/index.ts";
export * from "./currencies.ts";
export * from "./dynamic-data.ts";
export * from "./expression.ts";
export * from "./project-query.ts";
export * as projectQueryCursor from "./project-query-cursor.ts";
export * as projectQueryDiagnostics from "./project-query-explain.ts";

// D.2 — contracts draft; không mở endpoint hay authority runtime từ các exports này.
export * as workOs from "./work-os/index.ts";
export * as appSurfaces from "./app-surfaces/index.ts";

export * as aiModelControl from "./ai-model-control.ts";

export * as aiPreparationContext from "./ai-preparation-context.ts";

export * as aiPreparationReply from "./ai-preparation-reply.ts";

export * as aiPreparationInvocation from "./ai-preparation-invocation.ts";

export * as aiPreparationWakeup from "./ai-preparation-wakeup.ts";

export * as aiNativeUsage from "./ai-native-usage.ts";
export * as aiAccounting from "./ai-accounting.ts";
export * as aiAccountingWakeup from "./ai-accounting-wakeup.ts";
export * as runtimeMaintenanceTick from "./runtime-maintenance-tick.ts";

export * as aiPreparationArtifact from "./ai-preparation-artifact.ts";

export * as aiPreparationChildReply from "./ai-preparation-child-reply.ts";

export * as aiPreparationFanIn from "./ai-preparation-fan-in.ts";
export * from "./project-query-source.ts";

export * from './record-command.ts';

export * from './record-action.ts';
export * from './project-query-selection-source.ts';

export * from "./project-query-record-source.ts";

export * from "./project-query-metric-source.ts";

export * as aiPlanCandidate from "./ai-plan-candidate.ts";
